import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ToggleDocumentDownloadPack } from "../../../../Component/Api/DocumentUpload/DocumentUploadHelper";
import { CommitteeService } from "../../../../Component/Api/EmploymentEquity/Committee/CommitteeService";
import { ConfirmationDialog } from "../../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { DocumentsForDownloadParameters } from "../../../../Component/Model/Dto/Document";
import { CommitteeMinutes, CommitteeSearchRequest } from "../../../../Component/Model/Dto/EmploymentEquity/Committee";
import { CommitteeType } from "../../../../Component/Model/Enum/EmploymentEquity";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../Helpers/typescriptHelper";
import { ScorecardAtom } from "../../../../State/Scorecard.Atom";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { CommitteeMinutesModal } from "./CommitteeMinutesModal";
import { CommitteeMinutesTableMapper } from "./CommitteeMinutesTableColumn";

interface IDropDownOptions {
    value: string,
    id: CommitteeType
}

export const CommitteeMinutesPage = () => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [documentsPopUp, setDocumentsPopUp] = React.useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [minutesData, setMinutesData] = React.useState<CommitteeMinutes.SearchResponseRow[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedMinutesId, setSelectedMinutesId] = React.useState<string>("");
    const [selectedDocumentId, setSelectedDocumentId] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [selectedCommitteeType, setSelectedCommitteeType] = React.useState<CommitteeType>(CommitteeType.Forum);
    const scorecard = useRecoilValue(ScorecardAtom);

    const typeOptions: IDropDownOptions[] =
        [
            { value: "Forum", id: CommitteeType.Forum },
            { value: "Trade Union", id: CommitteeType.Trade_Union },
            { value: "All Employees", id: CommitteeType.All_Employees }
        ]

    const link = 'CommitteeMinutes';
    const scorecardId = scorecard.scorecardId;
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the minutes and all the associated supporting documents?"


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const handleCloseForm = () => {
        setPreviewPopup(false);
        setSelectedMinutesId("");
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedMinutesId("");
    }

    const handleEdit = (id: string) => {
        setSelectedMinutesId(id);
        setPreviewPopup(true);
    }

    const handleConfirmDelete = (id: string) => {
        setSelectedMinutesId(id);
        setShowConfirmationDialog(true);
    }

    const handleDelete = async () => {

        try {
            await CommitteeService.DeleteMinutes(selectedMinutesId);
            toast.success("Minutes deleted succesfully")
            fetchData();

        } catch (error) {
            console.log(error)
        }
        handleCloseConfirmation();

    }

    const handleAttachedSD = (id: string) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const handleToggleDocumentPack = async (id: string) => {

        let added = minutesData.find(objective => objective.id === id)?.documentsSelectedForDownload;
        setSelectedMinutesId(id);
        if (added !== undefined) {

            const toggleDocumentsParamters: DocumentsForDownloadParameters = {
                entityId: id,
                scorecardId: scorecardId,
                link: link
            };

            await ToggleDocumentDownloadPack(toggleDocumentsParamters, added);
            fetchData();
        }

    }

    const onDocumentPopupClose = () => {
        setDocumentsPopUp(false);
        fetchData();
    }

    const minutessSearchRequest: CommitteeSearchRequest = {
        pageNumber: pageNumber as number,
        pageSize: entriesPerPage.defaultValue as number,
        scorecardId: scorecardId,
        columnSortOptions: columnSortOptions,
        committeeType: selectedCommitteeType
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let searchResult = await CommitteeService.SearchMinutes(minutessSearchRequest);
            if (searchResult.data) {
                setMinutesData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);

            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions, selectedCommitteeType]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddMinutes = () => {
        setPreviewPopup(true)
    }
    const bulkActions = [
        {
            label: "Add New Minutes",
            action: handleAddMinutes
        }
    ]

    const committeeMinutesTableData = CommitteeMinutesTableMapper(minutesData, handleEdit, handleConfirmDelete, handleAttachedSD, handleToggleDocumentPack);

    return (
        <>
            <Grid container>
                <Grid item xs={6} sm={6} md={6} lg={6}>
                    <MDBox mb={2}>
                        <FormControl fullWidth >
                            <InputLabel>{"Type"}</InputLabel>
                            <Select
                                value={selectedCommitteeType}
                                label={"Type"}
                                onChange={(e: any) => {
                                    setSelectedCommitteeType(e.target.value)
                                }}
                                sx={{
                                    padding: "0.75rem 0 !important",
                                    fontSize: "0.875rem"
                                }}
                                size={"medium"}
                            >
                                {typeOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>{item.value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </MDBox>
                </Grid>
            </Grid>

            <DataTable
                key={"CommitteeMinutesTable"}
                table={committeeMinutesTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                bulkActions={bulkActions}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            ></DataTable>

            {previewPopup && <CommitteeMinutesModal
                previewPopup={previewPopup}
                id={selectedMinutesId !== "" ? selectedMinutesId : undefined}
                link={link} scorecardId={scorecardId}
                handleCancel={handleCloseForm}
                refreshTable={fetchData}
                setMinutesId={setSelectedMinutesId}
                committeeType={selectedCommitteeType} />}

            {documentsPopUp && <SupportingDocuments display={documentsPopUp} handleCancel={() => onDocumentPopupClose()} link={link} id={selectedDocumentId} scorecardId={scorecardId} isEEModule={true}/>}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </>
    )
}